/**
 * Allowance: what each child is paid, how often, and from when.
 *
 * It replaced the single monthly deposit. A plan belongs to one child, or to
 * the family pot (where the old monthly deposit now lives). The server pays
 * each plan on its day, on the family's own calendar, once -- see
 * services/api/valley/allowance.py.
 *
 * TWO HALVES. The summary is what a parent needs most days: who gets what,
 * when next, and one tap to pause, resume or stop -- for one child or for
 * everyone. The editor is for setting it up or changing it, either as one
 * amount for everyone or a different one for each child.
 *
 * Pausing never back-pays: resuming starts again from the next payday. The
 * plans are saved as a whole set (`saveAllowance`), so "pause everyone" is
 * one write and a plan left out is a plan stopped.
 */

import { useState } from 'react';
import {
  FAMILY_POT,
  saveAllowance,
  type AllowancePlan,
  type Every,
  type Person,
} from '../family/state';
import { amountInputMode, asTyped, dollars, isWhole, looksLikeAmount, normaliseAmount } from '../family/money';
import { localToday, readableDate } from '../family/goals';

type Sent = Record<string, { amount: string; every: Every; start: string; paused: boolean }>;

const OFTEN: Record<Every, string> = { week: 'every week', fortnight: 'every two weeks', month: 'every month' };
const OFTEN_CHOICES: Array<[Every, string]> = [['week', 'Weekly'], ['fortnight', 'Every 2 weeks'], ['month', 'Monthly']];

/** Plans as the API takes them back. */
function asSent(plans: Record<string, AllowancePlan>): Sent {
  return Object.fromEntries(
    Object.entries(plans).map(([who, plan]) => [
      who,
      { amount: asTyped(plan.amount), every: plan.every, start: plan.start, paused: plan.paused },
    ]),
  );
}

export function Allowance({
  familyId,
  people,
  plans,
  busy,
  onChanged,
}: {
  familyId: string;
  people: Person[];
  plans: Record<string, AllowancePlan>;
  busy: boolean;
  onChanged: (work: () => Promise<void>) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [stopping, setStopping] = useState(false);
  const name = (who: string) =>
    who === FAMILY_POT ? 'Family pot' : people.find((p) => p.id === who)?.name || 'Somebody';
  const order = [...people.map((p) => p.id), FAMILY_POT].filter((who) => plans[who]);
  const anyRunning = order.some((who) => !plans[who]!.paused);

  function save(next: Sent, after?: () => void): void {
    onChanged(async () => {
      await saveAllowance(familyId, next);
      after?.();
    });
  }
  const current = asSent(plans);

  return (
    <>
      <h2>Allowance</h2>
      {order.length === 0 && !editing ? (
        <p className="quiet">
          No allowance yet. Set one amount for everyone, or a different one for
          each child &mdash; weekly, every two weeks or monthly. It is added on
          its day, automatically.
        </p>
      ) : null}

      {order.length && !editing ? (
        <ul className="allowance-list">
          {order.map((who) => {
            const plan = plans[who]!;
            return (
              <li key={who} className={plan.paused ? 'allowance-row paused' : 'allowance-row'}>
                <div>
                  <p className="allowance-who">{name(who)}</p>
                  <p className="allowance-what">
                    {dollars(plan.amount)} {OFTEN[plan.every]} &middot;{' '}
                    {plan.paused ? <strong>Paused</strong> : <>next {readableDate(plan.next)}</>}
                  </p>
                </div>
                <span className="secret-actions">
                  <button
                    type="button"
                    className="text-button"
                    disabled={busy}
                    onClick={() => save({ ...current, [who]: { ...current[who]!, paused: !plan.paused } })}
                  >
                    {plan.paused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    type="button"
                    className="text-button danger"
                    disabled={busy}
                    onClick={() => {
                      const rest = { ...current };
                      delete rest[who];
                      save(rest);
                    }}
                  >
                    Stop
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {order.length && !editing ? (
        <div className="actions">
          <button type="button" disabled={busy} onClick={() => setEditing(true)}>
            Change allowance
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              save(Object.fromEntries(Object.entries(current).map(([who, p]) => [who, { ...p, paused: anyRunning }])))
            }
          >
            {anyRunning ? 'Pause everyone' : 'Resume everyone'}
          </button>
          <button type="button" className="text-button danger" disabled={busy} onClick={() => setStopping(true)}>
            Stop all&hellip;
          </button>
        </div>
      ) : null}

      {stopping ? (
        <div className="danger-box">
          <p>
            Stop every allowance? Nothing already paid changes. You can set it
            up again any time.
          </p>
          <div className="actions">
            <button type="button" className="danger-button" disabled={busy} onClick={() => save({}, () => setStopping(false))}>
              Stop them all
            </button>
            <button type="button" disabled={busy} onClick={() => setStopping(false)}>
              Keep them
            </button>
          </div>
        </div>
      ) : null}

      {editing || order.length === 0 ? (
        editing ? (
          <AllowanceEditor
            people={people}
            plans={plans}
            busy={busy}
            onCancel={() => setEditing(false)}
            onSave={(next) => save(next, () => setEditing(false))}
          />
        ) : (
          <button type="button" className="full add-person" onClick={() => setEditing(true)}>
            + Set up allowance
          </button>
        )
      ) : null}
    </>
  );
}

interface Draft {
  on: boolean;
  amount: string;
  every: Every;
  start: string;
}

/**
 * Setting it up or changing it. "Same for everyone" writes one plan per
 * child with the same values; "different for each" is a row per child. The
 * family pot's plan, if there is one, is kept as it is by the first and is a
 * row of its own in the second.
 */
function AllowanceEditor({
  people,
  plans,
  busy,
  onSave,
  onCancel,
}: {
  people: Person[];
  plans: Record<string, AllowancePlan>;
  busy: boolean;
  onSave: (plans: Sent) => void;
  onCancel: () => void;
}) {
  const today = localToday();
  const childPlans = people.map((p) => plans[p.id]).filter(Boolean) as AllowancePlan[];
  const alike =
    childPlans.length === people.length &&
    childPlans.every(
      (p) => p.amount === childPlans[0]!.amount && p.every === childPlans[0]!.every && p.start === childPlans[0]!.start,
    );
  const [mode, setMode] = useState<'same' | 'each'>(childPlans.length === 0 || alike ? 'same' : 'each');
  const first = childPlans[0];
  const [same, setSame] = useState<Draft>({
    on: true,
    amount: first ? asTyped(first.amount) : '',
    every: first?.every ?? 'week',
    start: first?.start ?? today,
  });
  const rows = [...people.map((p) => p.id), ...(plans[FAMILY_POT] ? [FAMILY_POT] : [])];
  const [each, setEach] = useState<Record<string, Draft>>(() =>
    Object.fromEntries(
      rows.map((who) => {
        const plan = plans[who];
        return [
          who,
          plan
            ? { on: true, amount: asTyped(plan.amount), every: plan.every, start: plan.start }
            : { on: false, amount: '', every: 'week', start: today },
        ];
      }),
    ),
  );
  const name = (who: string) =>
    who === FAMILY_POT ? 'Family pot' : people.find((p) => p.id === who)?.name || 'Somebody';

  const okDraft = (d: Draft) => !d.on || (looksLikeAmount(d.amount) && Number(normaliseAmount(d.amount)) > 0 && d.start !== '');
  const ready = mode === 'same' ? okDraft(same) && people.length > 0 : rows.every((who) => okDraft(each[who]!));
  const paused = (who: string) => Boolean(plans[who]?.paused);

  function build(): Sent {
    const out: Sent = {};
    const put = (who: string, d: Draft) => {
      out[who] = { amount: normaliseAmount(d.amount), every: d.every, start: d.start, paused: paused(who) };
    };
    if (mode === 'same') {
      for (const p of people) put(p.id, same);
      if (plans[FAMILY_POT]) {
        const fam = plans[FAMILY_POT];
        out[FAMILY_POT] = { amount: asTyped(fam.amount), every: fam.every, start: fam.start, paused: fam.paused };
      }
    } else {
      for (const who of rows) if (each[who]!.on) put(who, each[who]!);
    }
    return out;
  }

  const fields = (d: Draft, change: (d: Draft) => void, label: string) => (
    <div className="allowance-fields">
      <label className="field">
        <span>How much{label ? ` for ${label}` : ''}?</span>
        <input
          value={d.amount}
          onChange={(event) => change({ ...d, amount: event.target.value })}
          inputMode={amountInputMode()}
          autoComplete="off"
          placeholder={isWhole() ? '5' : '5.00'}
          aria-label={`Allowance amount${label ? ` for ${label}` : ''}`}
        />
      </label>
      <div className="segmented" role="group" aria-label="How often">
        {OFTEN_CHOICES.map(([every, words]) => (
          <button key={every} type="button" className={d.every === every ? 'on' : ''} onClick={() => change({ ...d, every })}>
            {words}
          </button>
        ))}
      </div>
      <label className="field">
        <span>First payday</span>
        <input type="date" value={d.start} onChange={(event) => change({ ...d, start: event.target.value })} />
        <small>
          {d.every === 'month'
            ? 'Then the same day each month (or the last day, in a short month).'
            : `Then every ${d.every === 'week' ? 'week' : 'two weeks'} on the same weekday.`}
        </small>
      </label>
    </div>
  );

  return (
    <div className="record allowance-editor">
      <div className="segmented">
        <button type="button" className={mode === 'same' ? 'on' : ''} onClick={() => setMode('same')}>
          Same for everyone
        </button>
        <button type="button" className={mode === 'each' ? 'on' : ''} onClick={() => setMode('each')}>
          Different for each
        </button>
      </div>

      {mode === 'same' ? (
        <>
          {fields(same, setSame, '')}
          <p className="footnote">
            {people.length
              ? `Each of ${people.map((p) => p.name || 'Somebody').join(', ')} gets this.`
              : 'Add somebody to the family first.'}
          </p>
        </>
      ) : (
        rows.map((who) => {
          const d = each[who]!;
          const change = (next: Draft) => setEach((all) => ({ ...all, [who]: next }));
          return (
            <div key={who} className="allowance-each">
              <label className="allowance-toggle">
                <input type="checkbox" checked={d.on} onChange={(event) => change({ ...d, on: event.target.checked })} />
                <span>{name(who)}{who === FAMILY_POT ? '' : ' gets an allowance'}</span>
              </label>
              {d.on ? fields(d, change, name(who)) : null}
            </div>
          );
        })
      )}

      <p className="footnote">
        Nothing is back-paid: a first payday in the past starts from the next
        one. Paused allowances stay paused.
      </p>
      <div className="actions">
        <button aria-busy={busy} type="button" className="primary" disabled={busy || !ready} onClick={() => onSave(build())}>
          {busy ? 'Saving…' : 'Save allowance'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
